import React, { useEffect, useRef } from 'react';
import { ArrowRight, ArrowDown, Radio, Satellite } from 'lucide-react';
import { gsap } from '../../hooks/useGsap';
import MagneticButton from '../ui/MagneticButton';
import Marquee from '../ui/Marquee';
import SatelliteField from '../ui/SatelliteField';

const Hero = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      const words = rootRef.current.querySelectorAll('[data-anim="word"]');
      gsap.set(words, { yPercent: 110, opacity: 0 });

      tl.to('.hero-eyebrow', { y: 0, opacity: 1, duration: 0.8 }, 0.15)
        .to(words, { yPercent: 0, opacity: 1, duration: 1, stagger: 0.06 }, 0.2)
        .to('.hero-paragraph', { y: 0, opacity: 1, duration: 0.9 }, 0.7)
        .to('.hero-ctas > *', { y: 0, opacity: 1, duration: 0.7, stagger: 0.1 }, 0.85)
        .to('.hero-telemetry', { y: 0, opacity: 1, duration: 0.9 }, 0.6)
        .to('.hero-badge', { y: 0, opacity: 1, duration: 0.8, stagger: 0.1 }, 0.4)
        .to('.hero-scroll', { y: 0, opacity: 1, duration: 0.8 }, 1.1);

      const live = rootRef.current.querySelector('[data-live-ndvi]');
      if (live) {
        const id = setInterval(() => {
          const v = 0.62 + Math.random() * 0.32;
          live.textContent = v.toFixed(2);
        }, 1400);
        return () => clearInterval(id);
      }
    }, rootRef);

    return () => ctx.revert();
  }, []);

  const headline = ['Transformando', 'o', 'AGRO', 'com', 'Tecnologia'];
  const partners = ['NERO Chain Labs', 'Morro Verde', 'Atvos', 'Effatha', 'Agrominas', 'Mosaic', 'Syngenta'];

  return (
    <section
      id="home"
      ref={rootRef}
      className="relative pt-36 pb-16 md:pt-44 md:pb-24 overflow-hidden bg-night-950 grain min-h-[100vh]"
    >
      <SatelliteField />

      {/* The content layer lets the canvas receive mouse events on empty areas */}
      <div className="container-custom relative z-10 flex flex-col gap-10 pointer-events-none">
        {/* Top badges */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <span
            className="hero-badge pill pointer-events-auto"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            <Satellite size={12} className="text-primary-400" />
            ATI Field Sensor / live
          </span>
          <span
            className="hero-badge pointer-events-auto inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-mist-400"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            <span className="ati-live" />
            EST · 2025 
          </span>
        </div>

        {/* Eyebrow */}
        <div
          className="hero-eyebrow"
          style={{ opacity: 0, transform: 'translateY(20px)' }}
        >
          <span className="eyebrow pointer-events-auto">
            A primeira liga universitária AgriTech do Brasil
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-display font-bold text-mist-50 tracking-tightest leading-[1.05] text-[clamp(2.6rem,9vw,8.5rem)]">
          {headline.map((word, i) => (
            <span
              key={i}
              className="inline-block overflow-hidden align-baseline mr-3 md:mr-6 pt-[0.32em] pb-[0.12em] -my-[0.22em]"
            >
              <span
                data-anim="word"
                className={
                  'inline-block word-warp pointer-events-auto ' +
                  (word === 'AGRO' ? 'text-gradient pr-[0.08em]' : '')
                }
              >
                {word === 'AGRO' ? 'Agronegócio' : word}
              </span>
            </span>
          ))}
        </h1>

        {/* Sub + CTAs + Telemetry */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 space-y-8">
            <div
              className="hero-paragraph max-w-xl"
              style={{ opacity: 0, transform: 'translateY(20px)' }}
            >
              <p className="text-lg md:text-xl text-mist-300 leading-relaxed">
                A primeira liga estudantil 100% focada em desenvolver soluções tecnológicas
                de código aberto para os desafios reais do agronegócio brasileiro.
              </p>
            </div>

            <div className="hero-ctas flex flex-col sm:flex-row gap-4">
              <MagneticButton
                href="#cases"
                strength={0.2}
                className="btn-primary pointer-events-auto"
                style={{ opacity: 0, transform: 'translateY(20px)' }}
              >
                Conheça nossos Cases
                <ArrowRight size={18} />
              </MagneticButton>
              <MagneticButton
                href="#about"
                strength={0.2}
                className="btn-secondary pointer-events-auto"
                style={{ opacity: 0, transform: 'translateY(20px)' }}
              >
                Saiba Mais
              </MagneticButton>
            </div>
          </div>

          {/* Telemetry panel */}
          <div
            className="hero-telemetry lg:col-span-5 pointer-events-auto"
            style={{ opacity: 0, transform: 'translateY(20px)' }}
          >
            <div className="ati-telemetry p-5 md:p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-mist-400">
                  <span className="ati-live" />
                  Field Readings
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-primary-400">
                  <Radio size={12} />
                  Synced
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-mist-500">NDVI · médio</div>
                  <div className="mt-1 font-display text-3xl md:text-4xl font-bold text-mist-50">
                    <span data-live-ndvi>0.78</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-mist-500">Cases entregues</div>
                  <div className="mt-1 font-display text-3xl md:text-4xl font-bold text-mist-50">9<span className="text-primary-400">+</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-mist-500">Integrantes</div>
                  <div className="mt-1 font-display text-3xl md:text-4xl font-bold text-mist-50">40<span className="text-primary-400">+</span></div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-mist-500">Open source</div>
                  <div className="mt-1 font-display text-3xl md:text-4xl font-bold text-mist-50">100<span className="text-primary-400">%</span></div>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-white/[0.07]">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-mist-500 mb-3">Parceiros ativos</div>
                <div className="flex flex-wrap gap-1.5">
                  {['NERO', 'Morro Verde', 'Atvos', 'Effatha', 'Agrominas', 'Mosaic', 'Syngenta'].map((p) => (
                    <span
                      key={p}
                      className="px-2 py-1 rounded-md bg-primary-400/10 border border-primary-400/20 text-[10px] font-mono uppercase tracking-wider text-primary-300"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div
          className="hero-scroll pointer-events-auto flex items-center justify-center gap-2 text-mist-500 text-[10px] uppercase tracking-[0.25em] pt-2"
          style={{ opacity: 0, transform: 'translateY(20px)' }}
        >
          <span>scroll para explorar</span>
          <ArrowDown size={14} className="animate-bounce" />
        </div>
      </div>

      {/* Marquee */}
      <div className="relative z-10 mt-16 border-y border-white/[0.07] bg-night-900/70 backdrop-blur-sm">
        <Marquee speed={45} className="py-5">
          {partners.map((p, i) => (
            <span
              key={i}
              className="flex items-center gap-12 text-mist-100 font-display text-2xl md:text-4xl font-bold tracking-tight"
            >
              {p}
              <span className="inline-block w-2 h-2 rounded-full bg-primary-400" />
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

export default Hero;
